import { redirectByNotification } from 'app/notifications';

import { UnreachableError, UnreachableFunction } from 'ui/base/types';
import { notificationToast } from 'ui/base/toast';

import { FCMService } from 'services/accounts/fcm_service';
import { GlobalAccountsDomain } from 'services/accounts/accounts_domain';

import {
  updateOnlineStatus,
  updateRestoreStatus,
  updateUpgradeStatus,
} from 'services/accounts/pangolin_service';

import { HostsService } from 'services/pangolin/hosts_service';
import { IssuesService } from 'services/pangolin/issues_service';
import { GlobalPangolinDomains } from 'services/pangolin/pangolin_domain';
import { loadAllPangolinDomains, loadPangolinDomain } from 'services/pangolin/loaders';

import { HistoryService } from './history';

import {
  ClipboardSetRequest,
  FCMMessage,
  FCMMessageKind,
  FCMTokenNativeRequest,
  IPCMessage,
  IPCMessageKind,
  NativeRequest,
  NativeRequestKind,
  NativeResponse,
  QRCodeNativeRequest,
} from './ipc_types';

import {
  configureInitialNotification,
  HISTORY,
  INITIAL_LOAD_COMPLETE,
  PLATFORM,
} from './config';

// Type definitions for non-standard globals that we expect.
// The premise here is that we call window.ReactNativeWebView.postMessage to send a string
// to the native application. The string encodes a javascript object that is deserialized
// on the other side.
//
// The native application communicates back by calling the two global functions:
// window.resolveNativeRequest or window.rejectNativeRequest
// Those will resolve any promises registered in pendingNativeRequests
type ReactNativeWebView = {
  postMessage: (message: string) => void;
};

type Resolver = {
  resolve: (response: NativeResponse) => void;
  reject: (reason?: string) => void;
};

declare global {
  interface Window {
    ReactNativeWebView: ReactNativeWebView | undefined;
    initiateNativeRequest: (requestJSON: string) => void;
    resolveNativeRequest: (responseJSON: string) => void;
    rejectNativeRequest: (requestID: string, reason?: string) => void;
    receiveIPCMessage: (messageJSON: string) => void;
  }
}

const pendingNativeRequests: Map<string, Resolver> = new Map();

// Registers the native request with our list of requests.
// Then submit the request through ReactNativeWebView.postMessage if available.
// Returns true if it was done, false otherwise.
// Also rejects immediately if ReactNativeWebView is not available
function transmitOutgoingNativeRequest(request: NativeRequest, resolver: Resolver): boolean {
  const ReactNativeWebView = window.ReactNativeWebView;
  if (ReactNativeWebView == null) {
    resolver.reject();
    return false;
  }

  pendingNativeRequests.set(request.id, resolver);
  const message: IPCMessage = {
    kind: IPCMessageKind.INITIATE_REQUEST,
    request,
  };

  ReactNativeWebView.postMessage(JSON.stringify(message));
  return true;
}

export function transmitOutgoingMessage(message: IPCMessage): boolean {
  const ReactNativeWebView = window.ReactNativeWebView;
  if (ReactNativeWebView == null) {
    return false;
  }
  ReactNativeWebView.postMessage(JSON.stringify(message));
  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PullToRefreshCallback = () => any;

const defaultPullToRefreshCallback = async() => {
  await loadAllPangolinDomains({
    url: new URL(window.location.href),
  });
};

let lastPullToRefreshEnabled = false;
let lastPullToRefreshCallback = defaultPullToRefreshCallback;
export function togglePullToRefresh(enabled: boolean, callback?: PullToRefreshCallback) {
  if (lastPullToRefreshEnabled === enabled) {
    return false;
  }
  lastPullToRefreshEnabled = enabled;
  lastPullToRefreshCallback = callback ?? defaultPullToRefreshCallback;
  return transmitOutgoingMessage({
    kind: IPCMessageKind.TOGGLE_REFRESH,
    refreshEnabled: enabled,
  });
}

// Global method used to resolve a pending promise
window.resolveNativeRequest = (responseJSON: string) => {
  const response: NativeResponse = JSON.parse(responseJSON);
  const resolver = pendingNativeRequests.get(response.id);
  if (resolver != null) {
    resolver.resolve(response);
    pendingNativeRequests.delete(response.id);
  }
};

// Global method used to reject a pending promise
window.rejectNativeRequest = (requestID: string, reason?: string) => {
  const resolver = pendingNativeRequests.get(requestID);
  if (resolver != null) {
    resolver.reject(reason);
    pendingNativeRequests.delete(requestID);
  }
};

window.initiateNativeRequest = async(requestJSON: string) => {
  const request: NativeRequest = JSON.parse(requestJSON);
  switch (request.kind) {
    case NativeRequestKind.HISTORY_CHECK:
      resolveIncomingNativeRequest({
        kind: NativeRequestKind.HISTORY_CHECK,
        id: request.id,
        canGoBack: HistoryService.canGoBack(),
      });
      break;
    case NativeRequestKind.RELOAD_DATA:
      await lastPullToRefreshCallback();
      resolveIncomingNativeRequest({
        kind: NativeRequestKind.RELOAD_DATA,
        id: request.id,
        success: true,
      });
      break;
    case NativeRequestKind.QR_CODE:
      rejectIncomingNativeRequest(request);
      break;
    case NativeRequestKind.FCM_TOKEN:
      rejectIncomingNativeRequest(request);
      break;
    case NativeRequestKind.CLIPBOARD_SET:
      rejectIncomingNativeRequest(request);
      break;
    default:
      UnreachableFunction(request);
  }
};

function resolveIncomingNativeRequest(response: NativeResponse): boolean {
  const ReactNativeWebView = window.ReactNativeWebView;
  if (ReactNativeWebView == null) {
    return false;
  }
  const message: IPCMessage = {
    kind: IPCMessageKind.RESOLVE_REQUEST,
    response,
  };
  ReactNativeWebView.postMessage(JSON.stringify(message));
  return true;
}

function rejectIncomingNativeRequest(request: NativeRequest): boolean {
  const ReactNativeWebView = window.ReactNativeWebView;
  if (ReactNativeWebView == null) {
    return false;
  }
  const message: IPCMessage = {
    kind: IPCMessageKind.REJECT_REQUEST,
    requestID: request.id,
  };
  ReactNativeWebView.postMessage(JSON.stringify(message));
  return true;
}

window.receiveIPCMessage = (messageJSON: string) => {
  const message: IPCMessage = JSON.parse(messageJSON);
  switch (message.kind) {
    case IPCMessageKind.FCM_TOKEN_REFRESH:
      FCMService.update(message.token);
      break;
    case IPCMessageKind.FCM_MESSAGE:
      handleNotification(message.message);
      break;
    case IPCMessageKind.FCM_MESSAGE_OPEN:
      handleNotificationOpen(message.message);
      break;
    // Do nothing for everything else
    case IPCMessageKind.INITIATE_REQUEST:
      break;
    case IPCMessageKind.RESOLVE_REQUEST:
      break;
    case IPCMessageKind.REJECT_REQUEST:
      break;
    case IPCMessageKind.TOGGLE_REFRESH:
      break;
    default:
      UnreachableFunction(message);
  }
};

// Generates a unique ID of length 64 used for identifying unique promises
function generateID(): string {
  const result = [];
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < 64; i++) {
    result.push(characters.charAt(Math.floor(Math.random() * characters.length)));
  }
  return result.join('');
}

// Requests a QR code and returns a promise
export function requestQRCode(): Promise<string> {
  return new Promise<string>((trueResolve, reject) => {
    // Create the request object
    const request: QRCodeNativeRequest = {
      kind: NativeRequestKind.QR_CODE,
      id: generateID(),
    };

    // Create a resolve function that extracts the result from the response
    const resolve = (response: NativeResponse) => {
      if (response.kind === NativeRequestKind.QR_CODE) {
        trueResolve(response.qrCode);
      } else {
        reject();
      }
    };

    // Transmit the request out
    transmitOutgoingNativeRequest(request, { resolve, reject });
  });
}

// Requests the FCM token and returns a promise
export function requestFCMToken(): Promise<string> {
  return new Promise<string>((trueResolve, reject) => {
    if (PLATFORM === 'web' && window.WebNotifications != null) {
      const promise = window.WebNotifications.registerRemoteNotifications();
      promise.then(trueResolve).catch(reject);
      return;
    }

    // Create the request object
    const request: FCMTokenNativeRequest = {
      kind: NativeRequestKind.FCM_TOKEN,
      id: generateID(),
    };

    // Create a resolve function that extracts the result from the response
    const resolve = (response: NativeResponse) => {
      if (response.kind === NativeRequestKind.FCM_TOKEN) {
        trueResolve(response.token);
      } else {
        reject();
      }
    };

    // Transmit the request out
    transmitOutgoingNativeRequest(request, { resolve, reject });
  });
}

// Requests the FCM token and returns a promise
export function setClipboard(value: string): Promise<boolean> {
  return new Promise<boolean>((trueResolve, reject) => {
    // Create the request object
    const request: ClipboardSetRequest = {
      kind: NativeRequestKind.CLIPBOARD_SET,
      id: generateID(),
      value,
    };

    // Create a resolve function that extracts the result from the response
    const resolve = (response: NativeResponse) => {
      if (response.kind === NativeRequestKind.CLIPBOARD_SET) {
        trueResolve(response.success);
      } else {
        reject();
      }
    };

    // Transmit the request out
    transmitOutgoingNativeRequest(request, { resolve, reject });
  });
}

export function handleNotification(notification: FCMMessage) {
  const id = notification.pangolinID;
  const pangolin = GlobalAccountsDomain.pangolins.get(id);
  const domain = GlobalPangolinDomains.domains.get(id);
  if (pangolin == null || domain == null) return;
  switch(notification.kind) {
    case FCMMessageKind.HOST_CONNECTED:
    case FCMMessageKind.NEW_INCIDENT: {
      // display the notification as a toast
      notificationToast(domain, notification);
      return;
    }
    case FCMMessageKind.ONLINE_STATUS_UPDATE: {
      // update the online status of the target pangolin
      updateOnlineStatus({
        id, domain: GlobalAccountsDomain,
        onlineStatus: notification.onlineStatus,
      });
      return;
    }
    case FCMMessageKind.UPGRADE_STATUS_UPDATE: {
      // update the upgrade status of the target pangolin
      updateUpgradeStatus({
        id, domain: GlobalAccountsDomain,
        upgradeID: notification.upgradeID,
        upgradeStatus: notification.upgradeStatus,
      });
      return;
    }
    case FCMMessageKind.RESTORE_STATUS_UPDATE:
      // update the restore status of the target pangolin
      updateRestoreStatus({
        id, domain: GlobalAccountsDomain,
        restoreID: notification.restoreID,
        restoreStatus: notification.restoreStatus,
      });
      return;
    default:
      throw new UnreachableError(notification);
  }
}

export async function handleNotificationOpen(notification: FCMMessage) {
  // If we haven't loaded yet, override the initial notification and piggy-back on the loader logic
  if (!INITIAL_LOAD_COMPLETE) { configureInitialNotification(notification); return; }
  // Try to redirect to the page which will handle the viewing of the notification
  if (await redirectByNotification(HISTORY, notification)) { return; }
  // If we fail to redirect, it's probably because the data hasn't loaded yet, so load it and try again
  const id = notification.pangolinID;
  const pangolin = GlobalAccountsDomain.pangolins.get(id);
  const domain = GlobalPangolinDomains.domains.get(id);
  if (pangolin == null || domain == null) { return; }
  switch(notification.kind) {
    case FCMMessageKind.HOST_CONNECTED: {
      // Try loading just the host data to see if that's enough
      await HostsService.fetch(domain, notification.hostID, true);
      if (await redirectByNotification(HISTORY, notification)) { return; }
      // If not, reload the entire pangolin domain and try again
      await loadPangolinDomain(pangolin, domain, { fcm: notification });
      if (await redirectByNotification(HISTORY, notification)) { return; }
      return;
    }
    case FCMMessageKind.NEW_INCIDENT: {
      // Try loading just the issue data to see if that's enough
      await IssuesService.fetchItem(domain, notification.issueID, true);
      if (await redirectByNotification(HISTORY, notification)) { return; }
      // If not, reload the entire pangolin domain and try again
      await loadPangolinDomain(pangolin, domain, { fcm: notification });
      if (await redirectByNotification(HISTORY, notification)) { return; }
      return;
    }
    case FCMMessageKind.ONLINE_STATUS_UPDATE:
    case FCMMessageKind.UPGRADE_STATUS_UPDATE:
    case FCMMessageKind.RESTORE_STATUS_UPDATE: {
      // the cloud will never send this as a notification so just ignore it
      return;
    }
    default: {
      throw new UnreachableError(notification);
    }
  }
}
